import { z } from 'zod';
import { useToast } from '@/components/ui/use-toast';
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases';

// Define form schema
const formSchema = z.object({
  name: z.string().min(2, {
    message: "Knowledge Base name must be at least 2 characters.",
  }),
  description: z.string().optional(),
  isPrivate: z.boolean().optional()
});

export type KnowledgeBaseFormValues = z.infer<typeof formSchema>;

export function useKnowledgeBaseOperations(
  fetchKnowledgeBaseDetails: () => Promise<void>,
  fetchKnowledgeBases: () => Promise<void>
) {
  const { toast } = useToast();
  const { 
    createKnowledgeBase, 
    updateKnowledgeBase, 
    deleteKnowledgeBase,
  } = useKnowledgeBases();
  
  const handleCreateKnowledgeBase = async (values: KnowledgeBaseFormValues) => {
    const newKbId = await createKnowledgeBase(
      values.name, 
      values.description || '', 
      undefined, 
      values.isPrivate || false
    );
    
    // Refresh knowledge bases list after creating
    if (newKbId !== null) {
      await fetchKnowledgeBases();
    }
    
    return newKbId !== null;
  };
  
  const handleUpdateKnowledgeBase = async (
    knowledgeBaseId: string, 
    values: KnowledgeBaseFormValues
  ) => {
    if (!knowledgeBaseId) return false;
    
    const updated = await updateKnowledgeBase(
      knowledgeBaseId, 
      values.name, 
      values.description || '', 
      values.isPrivate
    );
    
    if (updated) {
      // Refresh the knowledge base details
      await fetchKnowledgeBaseDetails();
      // Also refresh the whole list to update the sidebar
      await fetchKnowledgeBases();
      return true;
    }
    
    return false;
  };
  
  const handleDeleteKnowledgeBase = async (knowledgeBaseId: string) => {
    if (!knowledgeBaseId) return false;
    
    const deleted = await deleteKnowledgeBase(knowledgeBaseId);
    
    // Refresh knowledge bases list after deleting
    if (deleted) {
      await fetchKnowledgeBases();
    }
    
    return deleted;
  };

  return {
    formSchema,
    handleCreateKnowledgeBase,
    handleUpdateKnowledgeBase,
    handleDeleteKnowledgeBase
  };
}